import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {CalendarIcon} from "@radix-ui/react-icons";
import {Loader2} from "lucide-react";
import {format} from "date-fns";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {Input} from "@/components/ui/input";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import {ConfirmAction} from "@/components/ui/confirm-action-c.tsx";
import {Setting} from "@/types/Setting.ts";
import {getSettings, updateSetting} from "@/services/settingsService.ts";
import {generateMatches} from "@/services/suggestionService.ts";
import {getMentees} from "@/services/menteeService.ts";
import {
    sendMatchesAvailableEmail, sendMentorSelectionReminderEmail,
    sendMentorshipGroupAvailableEmail,
    sendPreferenceSubmissionReminderEmail
} from "@/services/emailService.ts";
import {setDeadline} from "@/utils/time-helper.ts";
import {Trans, useTranslation} from "react-i18next";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getUsers} from "@/services/userService.ts";
import {
    listDeadlineExtensions,
    createDeadlineExtension,
    removeDeadlineExtension,
    type CreateDeadlineExtensionDto,
    type DeadlineExtension,
} from "@/services/deadlineExtensionService.ts";
import {useToast} from "@/components/ui/use-toast.ts";
import {UserRole} from "@/types/UserRole.ts";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select.tsx";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {Trash2} from "lucide-react";

const defaultSpaceSetting: Setting = {
    id: 1,
    key: '',
    value: ''
};

const defaultPreferenceDeadlineSetting: Setting = {
    id: 2,
    key: '',
    value: ''
};

const defaultSelectionDeadlineSetting: Setting = {
    id: 3,
    key: '',
    value: ''
};

type ResponseData = {
    status: 'success' | 'failure';
    message: string;
}

const GeneralSettingsPage = () => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const [spaceSetting, setSpaceSetting] = useState<Setting>(defaultSpaceSetting);
    const [preferenceDeadlineSetting, setPreferenceDeadlineSetting] = useState<Setting>(defaultPreferenceDeadlineSetting);
    const [selectionDeadlineSetting, setSelectionDeadlineSetting] = useState<Setting>(defaultSelectionDeadlineSetting);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<ResponseData | null>(null);
    const [shouldSendMatchesAvailableEmails, setShouldSendMatchesAvailableEmails] = useState(false);
    const [shouldSendMentorshipGroupAvailableEmails, setShouldSendMentorshipGroupAvailableEmails] = useState(false);
    const [shouldSendPreferenceSubmissionReminderEmails, setShouldSendPreferenceSubmissionReminderEmails] = useState(false);
    const [shouldSendMentorSelectionReminderEmails, setShouldSendMentorSelectionReminderEmails] = useState(false);
    const [extensionToRemove, setExtensionToRemove] = useState<DeadlineExtension | null>(null);

    const queryClient = useQueryClient();
    const {toast} = useToast();

    const {data: extensions = []} = useQuery({
        queryKey: queryKeys.deadlineExtensions.all,
        queryFn: () => listDeadlineExtensions(),
    });

    const {data: users = []} = useQuery({
        queryKey: queryKeys.fetchUsers.all,
        queryFn: getUsers,
    });
    const mentees = users.filter((u) => u.role === UserRole.MENTEE);

    const createExtensionMutation = useMutation({
        mutationFn: (dto: CreateDeadlineExtensionDto) => createDeadlineExtension(dto),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: queryKeys.deadlineExtensions.all});
            queryClient.invalidateQueries({queryKey: queryKeys.effectiveDeadlines});
            extensionForm.reset();
            toast({description: t("settings.extensionGranted")});
        },
        onError: () => {
            toast({variant: "destructive", title: t("common.errorTitle"), description: t("common.errorDesc")});
        },
    });

    const removeExtensionMutation = useMutation({
        mutationFn: (id: number) => removeDeadlineExtension(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: queryKeys.deadlineExtensions.all});
            queryClient.invalidateQueries({queryKey: queryKeys.effectiveDeadlines});
            setExtensionToRemove(null);
            toast({description: t("settings.extensionRemoved")});
        },
        onError: () => {
            toast({variant: "destructive", title: t("common.errorTitle"), description: t("common.errorDesc")});
        },
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await getSettings();

                const space = data.find(setting => setting.id === defaultSpaceSetting.id) ?? defaultSpaceSetting;
                const preferenceDeadline = data.find(setting => setting.id === defaultPreferenceDeadlineSetting.id) ?? defaultPreferenceDeadlineSetting;
                const selectionDeadline = data.find(setting => setting.id === defaultSelectionDeadlineSetting.id) ?? defaultSelectionDeadlineSetting;

                setSpaceSetting(space);
                setPreferenceDeadlineSetting(preferenceDeadline);
                setSelectionDeadlineSetting(selectionDeadline);

                spaceForm.reset({
                    value: space.value
                });

                preferenceDeadlineForm.reset({
                    value: preferenceDeadline.value
                });

                selectionDeadlineForm.reset({
                    value: selectionDeadline.value
                });
            } catch (error) {
                console.error(`Error fetching Settings:`, error);
            }
        };

        fetchSettings();
    }, []);

    const SpaceFormSchema = z.object({
        value: z
            .coerce.number()
            .int({
                message: vt("integerOnly", {fieldName: t("settings.numberOfMentees")}),
            }).min(1, {
                message: vt("minValue", {fieldName: t("settings.numberOfMentees"), min: 1}),
            }).max(10, {
                message: vt("maxValue", {fieldName: t("settings.numberOfMentees"), max: 10}),
            }),
    })

    const PreferenceDeadlineFormSchema = z.object({
        value: z
            .date({
                required_error: vt("selectOne", {fieldName: t("common.date")}),
            })
        // .min(new Date(), {
        //     message: vt("cannotSelectPastDate")
        // }),
    })

    const SelectionDeadlineFormSchema = z.object({
        value: z
            .date({
                required_error: vt("selectOne", {fieldName: t("common.date")})
            })
        // .min(new Date(), {
        //     message: vt("cannotSelectPastDate")
        // }),
    })

    const ExtensionFormSchema = z.object({
        userId: z.coerce.number().int().positive(vt("selectOne", {fieldName: t("common.user")})),
        deadlineType: z.enum(["preference_submission", "mentor_selection"]),
        extendedUntil: z.date(),
        reason: z.string().optional(),
    })

    const spaceForm = useForm<z.infer<typeof SpaceFormSchema>>({
        resolver: zodResolver(SpaceFormSchema),
        mode: 'onChange',
    })

    const preferenceDeadlineForm = useForm<z.infer<typeof PreferenceDeadlineFormSchema>>({
        resolver: zodResolver(PreferenceDeadlineFormSchema),
        mode: 'onChange',
    })

    const selectionDeadlineForm = useForm<z.infer<typeof SelectionDeadlineFormSchema>>({
        resolver: zodResolver(SelectionDeadlineFormSchema),
        mode: 'onChange',
    })

    const extensionForm = useForm<z.infer<typeof ExtensionFormSchema>>({
        resolver: zodResolver(ExtensionFormSchema),
        defaultValues: {
            userId: undefined,
            deadlineType: "preference_submission",
            extendedUntil: undefined,
            reason: "",
        },
    })

    const handleSpaceFormSubmit = async (values: z.infer<typeof SpaceFormSchema>) => {
        const setting = {
            id: spaceSetting.id,
            key: spaceSetting.key,
            value: values.value
        }
        const updatedSetting = await updateSetting(setting.id, setting);
        setSpaceSetting(updatedSetting);
        toast({
            title: t("common.successTitle"),
            description: t("settings.updateSuccess"),
        })
    }

    const handlePreferenceDeadlineFormSubmit = async (values: z.infer<typeof PreferenceDeadlineFormSchema>) => {
        const setting = {
            id: preferenceDeadlineSetting.id,
            key: preferenceDeadlineSetting.key,
            value: setDeadline(values.value)
        }
        const updatedSetting = await updateSetting(setting.id, setting);
        setPreferenceDeadlineSetting(updatedSetting);
        toast({
            title: t("common.successTitle"),
            description: t("settings.updateSuccess"),
        })
    }

    const handleSelectionDeadlineFormSubmit = async (values: z.infer<typeof SelectionDeadlineFormSchema>) => {
        const setting = {
            id: selectionDeadlineSetting.id,
            key: selectionDeadlineSetting.key,
            value: setDeadline(values.value)
        }
        const updatedSetting = await updateSetting(setting.id, setting);
        setSelectionDeadlineSetting(updatedSetting);
        toast({
            title: t("common.successTitle"),
            description: t("settings.updateSuccess"),
        })
    }

    const handleExtensionFormSubmit = (values: z.infer<typeof ExtensionFormSchema>) => {
        createExtensionMutation.mutate({
            userId: values.userId,
            deadlineType: values.deadlineType,
            extendedUntil: setDeadline(values.extendedUntil).toISOString(),
            reason: values.reason || null,
        });
    }

    const handleRemoveExtensionConfirm = () => {
        if (extensionToRemove) {
            removeExtensionMutation.mutate(extensionToRemove.id);
        }
    }

    const handleGenerateMatches = async () => {
        setLoading(true);
        const result = await generateMatches();
        setResponse(result);
        setLoading(false);
    }

    useEffect(() => {
        if (response) {
            toast({
                variant: response.status === 'success' ? 'success' : 'destructive',
                title: response.status.toUpperCase(),
                description: response.message
            })
        }
    }, [response]);

    useEffect(() => {
        if (shouldSendMatchesAvailableEmails) {
            const fetchMentees = async () => {
                try {
                    const mentees = await getMentees();
                    await sendMatchesAvailableEmail(mentees, new Date(selectionDeadlineSetting.value).toDateString());
                    toast({
                        variant: 'success',
                        title: t("common.successTitle"),
                        description: t("settings.menteesNotified")
                    })
                } catch (error) {
                    toast({
                        variant: 'destructive',
                        title: t("common.errorTitle"),
                        description: t("common.errorDesc"),
                    })
                }
            };
            fetchMentees().catch(console.error);
            setShouldSendMatchesAvailableEmails(false);
        }
    }, [shouldSendMatchesAvailableEmails]);

    useEffect(() => {
        if (shouldSendMentorshipGroupAvailableEmails) {
            const sendEmail = async () => {
                try {
                    await sendMentorshipGroupAvailableEmail();
                    toast({
                        variant: 'success',
                        title: t("common.successTitle"),
                        description: 'Mentors have been notified',
                    })
                } catch (error) {
                    toast({
                        variant: 'destructive',
                        title: t("common.errorTitle"),
                        description: t("common.errorDesc"),
                    })
                }
            };
            sendEmail().catch(console.error);
            setShouldSendMentorshipGroupAvailableEmails(false);
        }
    }, [shouldSendMentorshipGroupAvailableEmails]);

    useEffect(() => {
        if (shouldSendPreferenceSubmissionReminderEmails) {
            const sendEmail = async () => {
                try {
                    await sendPreferenceSubmissionReminderEmail(new Date(preferenceDeadlineSetting.value).toDateString());
                    toast({
                        variant: 'success',
                        title: t("common.successTitle"),
                        description: 'Mentees have been notified',
                    })
                } catch (error) {
                    toast({
                        variant: 'destructive',
                        title: t("common.errorTitle"),
                        description: t("common.errorDesc"),
                    })
                }
            };
            sendEmail().catch(console.error);
            setShouldSendPreferenceSubmissionReminderEmails(false);
        }
    }, [shouldSendPreferenceSubmissionReminderEmails]);

    useEffect(() => {
        if (shouldSendMentorSelectionReminderEmails) {
            const sendEmail = async () => {
                try {
                    await sendMentorSelectionReminderEmail(new Date(selectionDeadlineSetting.value).toDateString());
                    toast({
                        variant: 'success',
                        title: t("common.successTitle"),
                        description: 'Mentees have been notified',
                    })
                } catch (error) {
                    toast({
                        variant: 'destructive',
                        title: t("common.errorTitle"),
                        description: t("common.errorDesc"),
                    })
                }
            };
            sendEmail().catch(console.error);
            setShouldSendMentorSelectionReminderEmails(false);
        }
    }, [shouldSendMentorSelectionReminderEmails]);

    const today = new Date();

    return (
        <div className="grid gap-6">
            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.preferenceSubmissionDeadline")}</CardTitle>
                    <CardDescription>
                        {t("settings.preferenceSubmissionDeadlineDesc")}
                    </CardDescription>
                </CardHeader>

                <Form {...preferenceDeadlineForm}>
                    <form onSubmit={preferenceDeadlineForm.handleSubmit(handlePreferenceDeadlineFormSubmit)}>
                        <CardContent>
                            <FormField
                                control={preferenceDeadlineForm.control}
                                name="value"
                                render={({field}) => (
                                    <FormItem className="flex flex-col">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant={"outline"}
                                                        className={cn(
                                                            "w-[240px] pl-3 text-left font-normal",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value ? (
                                                            format(field.value, "PPP")
                                                        ) : (
                                                            <span>{t("common.pickDate")}</span>
                                                        )}
                                                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50"/>
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    // disabled={date => date < new Date()}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                        <CardFooter className="border-t px-6 py-4">
                            <Button type="submit"
                                    disabled={!preferenceDeadlineForm.formState.isDirty || preferenceDeadlineForm.formState.isSubmitting}>
                                {t("common.save")}
                            </Button>
                        </CardFooter>
                    </form>
                </Form>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.mentorSelectionDeadline")}</CardTitle>
                    <CardDescription>
                        {t("settings.mentorSelectionDeadlineDesc")}
                    </CardDescription>
                </CardHeader>

                <Form {...selectionDeadlineForm}>
                    <form onSubmit={selectionDeadlineForm.handleSubmit(handleSelectionDeadlineFormSubmit)}>
                        <CardContent>
                            <FormField
                                control={selectionDeadlineForm.control}
                                name="value"
                                render={({field}) => (
                                    <FormItem className="flex flex-col">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant={"outline"}
                                                        className={cn(
                                                            "w-[240px] pl-3 text-left font-normal",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value ? (
                                                            format(field.value, "PPP")
                                                        ) : (
                                                            <span>{t("common.pickDate")}</span>
                                                        )}
                                                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50"/>
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    // disabled={date => date < new Date()}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                        <CardFooter className="border-t px-6 py-4">
                            <Button type="submit"
                                    disabled={!selectionDeadlineForm.formState.isDirty || selectionDeadlineForm.formState.isSubmitting}>
                                {t("common.save")}
                            </Button>
                        </CardFooter>
                    </form>
                </Form>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.deadlineExtensions")}</CardTitle>
                    <CardDescription>
                        {t("settings.deadlineExtensionsDesc")}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <Form {...extensionForm}>
                        <form onSubmit={extensionForm.handleSubmit(handleExtensionFormSubmit)} className="flex flex-wrap items-end gap-4">
                            <FormField
                                control={extensionForm.control}
                                name="userId"
                                render={({field}) => (
                                    <FormItem className="w-[200px]">
                                        <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value?.toString()}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("common.user")} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {mentees.map((u) => (
                                                    <SelectItem key={u.id} value={u.id.toString()}>
                                                        {u.fullName} ({u.email})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={extensionForm.control}
                                name="deadlineType"
                                render={({field}) => (
                                    <FormItem className="w-[220px]">
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="preference_submission">{t("settings.deadlineTypePreference")}</SelectItem>
                                                <SelectItem value="mentor_selection">{t("settings.deadlineTypeSelection")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={extensionForm.control}
                                name="extendedUntil"
                                render={({field}) => (
                                    <FormItem className="flex flex-col">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        className={cn("w-[240px] pl-3 text-left font-normal", !field.value && "text-muted-foreground")}
                                                    >
                                                        {field.value ? format(field.value, "PPP") : <span>{t("settings.extendedUntil")}</span>}
                                                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus />
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={extensionForm.control}
                                name="reason"
                                render={({field}) => (
                                    <FormItem className="min-w-[120px] flex-1">
                                        <FormControl>
                                            <Input placeholder={t("common.reason")} {...field} value={field.value ?? ""} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button type="submit" disabled={createExtensionMutation.isPending}>
                                {createExtensionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {t("settings.grantExtension")}
                            </Button>
                        </form>
                    </Form>
                    {extensions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t("settings.noExtensions")}</p>
                    ) : (
                        <div className="rounded-md border">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="p-2 text-left font-medium">{t("common.user")}</th>
                                        <th className="p-2 text-left font-medium">{t("common.type")}</th>
                                        <th className="p-2 text-left font-medium">{t("settings.extendedUntil")}</th>
                                        <th className="p-2 w-10" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {extensions.map((ext) => (
                                        <tr key={ext.id} className="border-b last:border-0">
                                            <td className="p-2">{ext.user.firstName} {ext.user.lastName} ({ext.user.email})</td>
                                            <td className="p-2">
                                                {ext.deadlineType === "preference_submission" ? t("settings.deadlineTypePreference") : t("settings.deadlineTypeSelection")}
                                            </td>
                                            <td className="p-2">{format(new Date(ext.extendedUntil), "PPP")}</td>
                                            <td className="p-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    title={t("common.remove")}
                                                    onClick={() => setExtensionToRemove(ext)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!extensionToRemove} onOpenChange={(open) => !open && setExtensionToRemove(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {extensionToRemove && (
                                <>Remove extension for {extensionToRemove.user.firstName} {extensionToRemove.user.lastName} ({extensionToRemove.deadlineType === "preference_submission" ? t("settings.deadlineTypePreference") : t("settings.deadlineTypeSelection")})?</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={removeExtensionMutation.isPending}>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleRemoveExtensionConfirm(); }}
                            disabled={removeExtensionMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t("common.remove")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.maxMenteesPerMentor")}</CardTitle>
                    <CardDescription>
                        {t("settings.maxMenteesPerMentorDesc")}
                    </CardDescription>
                </CardHeader>
                <Form {...spaceForm}>
                    <form onSubmit={spaceForm.handleSubmit(handleSpaceFormSubmit)} className="flex flex-col gap-4">
                        <CardContent>
                            <FormField
                                control={spaceForm.control}
                                name="value"
                                render={({field}) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input type="number"
                                                   placeholder={t("settings.maxMenteesPerMentorPlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                        <CardFooter className="border-t px-6 py-4">
                            <Button type="submit"
                                    disabled={!spaceForm.formState.isDirty || spaceForm.formState.isSubmitting}>
                                {t("common.save")}
                            </Button>
                        </CardFooter>
                    </form>
                </Form>
            </Card>

            <Card className="py-14">
                <div className="max-w-screen-xl mx-auto px-4 md:text-center md:px-8">
                    <div className="max-w-xl md:mx-auto">
                        <h3 className="text-xl font-semibold text-text-1">
                            {t("settings.generateMatches")}
                        </h3>
                        <p className="mt-3">
                            <span className="font-bold">{t("settings.caution")}</span> <Trans
                            i18nKey="settings.cautionDesc" components={{underline: <u/>}}/>
                        </p>
                    </div>
                    <div className="flex gap-3 items-center mt-4 md:justify-center">
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin"/> {t("common.processing")}
                            </>
                        ) : (
                            <>
                                <ConfirmAction
                                    triggerText={t("common.start")}
                                    clickHandler={() => handleGenerateMatches()}
                                    classNames="border rounded-md py-2 px-3 font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                                />

                                <ConfirmAction
                                    triggerText="Notify Mentees"
                                    clickHandler={() => setShouldSendMatchesAvailableEmails(true)}
                                    classNames="border rounded-md py-2 px-3 font-medium bg-secondary text-secondary-foreground hover:bg-secondary/90"
                                />
                            </>
                        )}
                    </div>
                </div>
            </Card>

            {today > new Date(selectionDeadlineSetting.value) &&
                <Card>
                    <CardHeader>
                        <CardTitle>Mentorship Groups Are Now Ready</CardTitle>
                        <CardDescription>
                            You may choose to notify the mentors. An email will be sent to mentors along with the URL to
                            their mentorship group page.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ConfirmAction
                            triggerText="Notify Mentors"
                            clickHandler={() => setShouldSendMentorshipGroupAvailableEmails(true)}
                            classNames="border rounded-md py-2 px-3 font-medium bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        />
                    </CardContent>
                </Card>
            }

            {today <= new Date(preferenceDeadlineSetting.value) &&
                <Card>
                    <CardHeader>
                        <CardTitle>Send Preference Submission Reminders</CardTitle>
                        <CardDescription>
                            This will send an email to all mentees who have not submitted their mentorship preference
                            yet. Email includes link to the preference submission page.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ConfirmAction
                            triggerText="Notify Mentees"
                            clickHandler={() => setShouldSendPreferenceSubmissionReminderEmails(true)}
                            classNames="border rounded-md py-2 px-3 font-medium bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        />
                    </CardContent>
                </Card>
            }

            {today > new Date(preferenceDeadlineSetting.value) && today <= new Date(selectionDeadlineSetting.value) &&
                <Card>
                    <CardHeader>
                        <CardTitle>Send Mentor Selection Reminders</CardTitle>
                        <CardDescription>
                            This will send an email to all mentees who have not selected a mentor yet. Email includes
                            link to the mentor selection page.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ConfirmAction
                            triggerText="Notify Mentees"
                            clickHandler={() => setShouldSendMentorSelectionReminderEmails(true)}
                            classNames="border rounded-md py-2 px-3 font-medium bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        />
                    </CardContent>
                </Card>
            }
        </div>
    )
}

export default GeneralSettingsPage;
